<template>
  <div :id="modalId" class="modal overflow-y-auto flex" tabindex="-1" aria-hidden="true" :class="{ show: isOpen }">
    <div class="modal-dialog">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header px-5 py-3 border-b border-gray-200 dark:border-dark-5">
          <h2 class="font-medium text-base">
            {{ $t("views.medication.quick_create.new_drug") }}
          </h2>
        </div>

        <!-- Modal Body -->
        <div class="modal-body px-5 py-6">
          <!-- Name Input -->
          <div>
            <label for="quick-drug-name" class="form-label font-semibold">
              {{ $t("views.medication.quick_create.drug_name") }}
            </label>
            <div class="input-group">
              <input
                id="quick-drug-name"
                type="text"
                class="form-control w-full bg-theme-32"
                :class="validationErrors.name ? 'border border-theme-6' : ''"
                :placeholder="$t('views.medication.quick_create.drug_name')"
                v-model="drug.name"
                maxlength="110"
                @input="clearValidationError('name')"
              />
            </div>
            <div v-if="validationErrors.name" class="text-theme-6 mt-1 text-sm font-semibold">
              {{ validationErrors.name }}
            </div>
          </div>

          <!-- Mechanism of Action Input -->
          <div class="mt-5">
            <label for="quick-drug-action-mechanism" class="form-label font-semibold">
              {{ $t("views.medication.quick_create.action_mechanism") }}
            </label>
            <div class="input-group">
              <textarea
                id="quick-drug-action-mechanism"
                rows="5"
                class="form-control w-full bg-theme-32"
                :class="validationErrors.action_mechanism ? 'border border-theme-6' : ''"
                :placeholder="$t('views.medication.quick_create.action_mechanism')"
                v-model="drug.action_mechanism"
                maxlength="2000"
                @input="clearValidationError('action_mechanism')"
              ></textarea>
            </div>
            <div v-if="validationErrors.action_mechanism" class="text-theme-6 mt-1 text-sm font-semibold">
              {{ validationErrors.action_mechanism }}
            </div>
          </div>

          <!-- API Error message -->
          <div v-if="apiError" class="text-theme-6 mt-3 text-sm font-semibold">
            {{ apiError }}
          </div>
        </div>

        <!-- Modal Footer -->
        <div
          class="modal-footer px-5 py-3 border-t border-gray-200 dark:border-dark-5 text-right flex justify-end gap-3"
        >
          <button type="button" @click="close" class="btn btn-outline-secondary w-24 mr-2" :disabled="loading">
            {{ $t("views.cancel") }}
          </button>
          <button
            type="button"
            @click="save"
            class="btn btn-primary w-24 flex items-center justify-center"
            :disabled="loading"
          >
            <LoadingIcon v-if="loading" icon="three-dots" class="w-4 h-4 ml-2" />
            <span v-else>{{ $t("views.save") }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import MedicationService from "@/services/medications.service";
import Toastify from "toastify-js";

export default defineComponent({
  name: "DrugQuickCreateModal",
  props: {
    modalId: {
      type: String,
      default: "drug-quick-create-modal",
    },
    medicationClassId: {
      type: [String, Number],
      required: true,
    },
  },
  emits: ["created"],
  data() {
    return {
      isOpen: false,
      loading: false,
      drug: {
        name: "",
        action_mechanism: "",
      },
      validationErrors: {
        name: "",
        action_mechanism: "",
      },
      apiError: "",
      lang_key: localStorage.lang ? localStorage.lang : navigator.language.split("-")[0],
    };
  },
  created() {
    this.resetForm();
  },
  methods: {
    resetForm() {
      this.drug = {
        name: "",
        action_mechanism: "",
      };
      this.validationErrors = {
        name: "",
        action_mechanism: "",
      };
      this.apiError = "";
    },
    show() {
      this.resetForm();
      this.isOpen = true;
      cash("#" + this.modalId).modal("show");
    },
    close() {
      this.isOpen = false;
      cash("#" + this.modalId).modal("hide");
    },
    clearValidationError(field) {
      this.validationErrors[field] = "";
    },
    async save() {
      this.validationErrors.name = "";
      this.validationErrors.action_mechanism = "";
      this.apiError = "";

      let hasErrors = false;

      if (!this.drug.name || !this.drug.name.trim()) {
        this.validationErrors.name = this.$t("views.medication.text13");
        hasErrors = true;
      }

      if (!this.drug.action_mechanism || !this.drug.action_mechanism.trim()) {
        this.validationErrors.action_mechanism = this.$t("views.medication.text25");
        hasErrors = true;
      }

      if (hasErrors) {
        return;
      }

      this.loading = true;

      try {
        const payload = {
          name: this.drug.name.trim(),
          action_mechanism: this.drug.action_mechanism.trim(),
          commercial_names: [],
          medication_classes: [this.medicationClassId],
        };

        const response = await MedicationService.save(payload, this.lang_key);

        let newDrugId = null;
        if (response && response.data && response.data.length > 0) {
          newDrugId = response.data[0].id;
        } else if (response && response.id) {
          newDrugId = response.id;
        }

        // Emit event to parent with the newly created drug details
        this.$emit("created", {
          id: newDrugId || response.id,
          name: this.drug.name.trim(),
          action_mechanism: this.drug.action_mechanism.trim(),
        });

        this.showToast();
        this.close();
      } catch (error) {
        console.error("Error creating medication drug via quick modal:", error);
        this.apiError = error?.response?.data?.message || error?.message || "Error al guardar el medicamento.";
      } finally {
        this.loading = false;
      }
    },
    showToast() {
      const customNode = cash(`
        <div class="toastify-content flex">
          <div class="text-theme-9">✓</div>
          <div class="ml-4 mr-4">
            <div class="font-medium">Guardado con éxito.</div>
          </div>
        </div>
      `)[0];
      Toastify({
        node: customNode,
        duration: 3000,
        gravity: "top",
        position: "right",
        close: true,
      }).showToast();
    },
  },
});
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
